import { useQuery } from '@tanstack/react-query'
import { Item, ItemMetadata } from 'types/nft'
import { fetchWithGatewayRetry } from 'utils/gatewayRetry'
import { getIpfsUrl } from 'utils/ipfs'
import { readInlineTokenUri, toTokenUriMetadata } from 'utils/nftItems'

type TokenMetadataSource = Pick<Item, 'metadata' | 'tokenUri'>

/** Loads the JSON a token URI points to: inline data URI, ipfs://, CID path or http(s). */
const fetchTokenUriMetadata = async (
  tokenUri: string
): Promise<ItemMetadata | null> => {
  const inline = readInlineTokenUri(tokenUri)
  if (inline !== undefined) {
    return toTokenUriMetadata(inline)
  }
  const url = getIpfsUrl(tokenUri)
  const response = await fetchWithGatewayRetry(url)
  if (!response.ok) {
    throw new Error(
      `Failed to load ${url}: ${response.status} ${response.statusText}`
    )
  }
  return toTokenUriMetadata(await response.json())
}

// fetchWithGatewayRetry retries gateway errors; a URI that still fails is
// not requested again on focus, reconnect or remount
const TOKEN_METADATA_QUERY_OPTIONS = {
  refetchOnReconnect: false,
  refetchOnWindowFocus: false,
  retry: false,
  retryOnMount: false,
  staleTime: Infinity
}

export const tokenUriMetadataQueryKey = (
  tokenUri: string | undefined
): unknown[] => ['tokenUriMetadata', tokenUri]

/** One cache entry per token URI, shared by every token that points to it. */
const useTokenUriQuery = (
  tokenUri: string | undefined,
  enabled: boolean
): ItemMetadata | null => {
  const { data } = useQuery(
    tokenUriMetadataQueryKey(tokenUri),
    () => (tokenUri ? fetchTokenUriMetadata(tokenUri) : null),
    { ...TOKEN_METADATA_QUERY_OPTIONS, enabled: enabled && !!tokenUri }
  )
  return data ?? null
}

/** Metadata from a token URI, for tokens the Data API has not indexed. */
export const useTokenUriMetadata = (
  tokenUri: string | undefined
): ItemMetadata | null => useTokenUriQuery(tokenUri, true)

/**
 * Returns the item's indexed metadata, or loads it from the token URI
 * when the Data API has none. Errors leave the metadata null.
 */
export const useTokenMetadata = (
  item: TokenMetadataSource | undefined
): ItemMetadata | null => {
  const fallback = useTokenUriQuery(
    item?.tokenUri ?? undefined,
    !!item && item.metadata === null
  )
  return item?.metadata ?? fallback
}

/**
 * Returns the item's indexed metadata, or the token URI metadata a mounted
 * card already loaded. Never starts a request, so a list of every selected
 * item does not fan out to the gateway.
 */
export const useCachedTokenMetadata = (
  item: TokenMetadataSource
): ItemMetadata | null => {
  const cached = useTokenUriQuery(item.tokenUri ?? undefined, false)
  return item.metadata ?? cached
}
